import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar, Icon, NAV, Wordmark } from './components';
import { useApp } from './app-provider';
import { ROUTE_FOR_NAV, ROUTES } from './routes';

const ASIDE_COLLAPSED_STORAGE_KEY = 'sp_aside_collapsed';
const SERVER_CLOCK_SYNC_INTERVAL_MS = 5 * 60 * 1000;

function formatServerClock(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '--:--:-- - --/--/----';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} - ${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function ShellDebugPanel() {
  const app = useApp();
  const isAdministrator = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const [backendFilter, setBackendFilter] = useState('');
  const [apiSiengeFilter, setApiSiengeFilter] = useState('');
  if (app.debugEnabled !== 'S') return null;
  const backendBase = app.debugBackendBaseUrl || '';
  const endpoints = Array.isArray(app.debugBackendEndpoints) ? app.debugBackendEndpoints : [];
  const backendFilterNorm = backendFilter.trim().toLowerCase();
  const filteredEndpoints = backendFilterNorm
    ? endpoints.filter((ep) => ep.toLowerCase().includes(backendFilterNorm))
    : endpoints;
  const siengeMap = app.debugApiSiengeByRoute && typeof app.debugApiSiengeByRoute === 'object'
    ? app.debugApiSiengeByRoute
    : {};
  const frontendFilesMap = app.debugFrontendFilesByRoute && typeof app.debugFrontendFilesByRoute === 'object'
    ? app.debugFrontendFilesByRoute
    : {};
  const frontendFilesByPage = Array.isArray(frontendFilesMap[app.route]) ? frontendFilesMap[app.route] : [];
  const frontendSharedFiles = Array.isArray(app.debugFrontendSharedFiles) ? app.debugFrontendSharedFiles : [];
  const frontendFilesLoaded = Array.from(new Set([...frontendFilesByPage, ...frontendSharedFiles]));
  const siengeEndpointsByPage = Array.isArray(siengeMap[app.route]) ? siengeMap[app.route] : [];
  const apiSiengeFilterNorm = apiSiengeFilter.trim().toLowerCase();
  const filteredSiengeEndpoints = apiSiengeFilterNorm
    ? siengeEndpointsByPage.filter((ep) => ep.toLowerCase().includes(apiSiengeFilterNorm))
    : siengeEndpointsByPage;
  return (
    <div className="sp-debug-wrap" role="status" aria-live="polite">
      <div className="sp-debug-title">
        DEBUG ativado{' '}
        {isAdministrator && (
          <button
            type="button"
            className="sp-debug-title-link"
            onClick={() => app.navigate(ROUTES.SETTINGS, { tab: 'debug' })}
          >
            [Clique Aqui]
          </button>
        )}
      </div>
      <div className="sp-debug-grid">
        <div className="sp-card sp-debug-card">
          <div className="sp-caps">1ª Rota da página carregada</div>
          <div className="sp-debug-line"><strong>route:</strong> <span className="sp-mono">{app.route || '—'}</span></div>
          <div className="sp-debug-line"><strong>data-page-route:</strong> <span className="sp-mono">{document.body?.getAttribute('data-page-route') || '—'}</span></div>
          <div className="sp-debug-line"><strong>body.id:</strong> <span className="sp-mono">{document.body?.id || '—'}</span></div>
          <div className="sp-debug-line"><strong>params:</strong> <span className="sp-mono">{JSON.stringify(app.params || {})}</span></div>
          <div className="sp-caps sp-debug-subtitle">2º Arquivos do FrontEnd Usados</div>
          <div className="sp-debug-links">
            {frontendFilesLoaded.map((file) => (
              <div key={file} className="sp-debug-link">
                <span className="sp-mono">{file}</span>
              </div>
            ))}
            {frontendFilesLoaded.length === 0 && (
              <div className="sp-debug-line">Nenhum arquivo de frontend mapeado para esta rota.</div>
            )}
          </div>
        </div>
        <div className="sp-card sp-debug-card">
          <div className="sp-caps">Backend</div>
          <div className="sp-debug-line"><strong>baseUrl:</strong> <span className="sp-mono">{backendBase || '—'}</span></div>
          <div className="sp-debug-filter-row">
            <input
              className="sp-input sp-debug-filter-input"
              placeholder="Filtrar por método ou endpoint (ex.: GET /api/companies)"
              value={backendFilter}
              onChange={(e) => setBackendFilter(e.target.value)}
            />
            <span className="sp-debug-filter-count sp-mono">{filteredEndpoints.length}/{endpoints.length}</span>
          </div>
          <div className="sp-debug-links">
            {filteredEndpoints.map((ep) => {
              const sep = ep.indexOf(' ');
              const method = sep > 0 ? ep.slice(0, sep) : 'GET';
              const path = sep > 0 ? ep.slice(sep + 1) : ep;
              const href = `${backendBase}${path.replace(/:[^/]+/g, '1')}`;
              return (
                <a key={ep} className="sp-debug-link" href={href} target="_blank" rel="noreferrer">
                  <span className="sp-debug-method">{method}</span>
                  <span className="sp-mono">{path}</span>
                </a>
              );
            })}
            {filteredEndpoints.length === 0 && (
              <div className="sp-debug-line">Nenhum endpoint encontrado para o filtro informado.</div>
            )}
          </div>
        </div>
        <div className="sp-card sp-debug-card">
          <div className="sp-caps">API SIENGE</div>
          <div className="sp-debug-line">
            <strong>rota atual:</strong> <span className="sp-mono">{app.route || '—'}</span>
          </div>
          <div className="sp-debug-filter-row">
            <input
              className="sp-input sp-debug-filter-input"
              placeholder="Filtrar chamadas externas"
              value={apiSiengeFilter}
              onChange={(e) => setApiSiengeFilter(e.target.value)}
            />
            <span className="sp-debug-filter-count sp-mono">
              {filteredSiengeEndpoints.length}/{siengeEndpointsByPage.length}
            </span>
          </div>
          <div className="sp-debug-links">
            {filteredSiengeEndpoints.map((ep) => {
              const sep = ep.indexOf(' ');
              const method = sep > 0 ? ep.slice(0, sep) : 'GET';
              const path = sep > 0 ? ep.slice(sep + 1) : ep;
              const href = `${backendBase}${path.replace(/:[^/]+/g, '1')}`;
              return (
                <a key={`sienge-${ep}`} className="sp-debug-link" href={href} target="_blank" rel="noreferrer">
                  <span className="sp-debug-method">{method}</span>
                  <span className="sp-mono">{path}</span>
                </a>
              );
            })}
            {siengeEndpointsByPage.length === 0 && (
              <div className="sp-debug-line">Está página não realiza chamadas a API DIENGE Externa</div>
            )}
            {siengeEndpointsByPage.length > 0 && filteredSiengeEndpoints.length === 0 && (
              <div className="sp-debug-line">Nenhum endpoint encontrado para o filtro informado.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const LAYOUT_CACHE_KEY = 'sp_login_layout_cache';
const MENU_LAYOUT_DEFAULTS = { menu_wm_brand: 'SIENGE', menu_wm_accent: 'PAY', menu_brand_sub: 'by FullTec' };

function readMenuLayoutCache() {
  try {
    const raw = localStorage.getItem(LAYOUT_CACHE_KEY);
    return raw ? { ...MENU_LAYOUT_DEFAULTS, ...JSON.parse(raw) } : null;
  } catch { return null; }
}

function ShellAside({ active, drawerOpen, portaledDrawer, setAsideOpen, onCollapse, canCollapse }) {
  const app = useApp();
  const [menuLayout, setMenuLayout] = useState(() => readMenuLayoutCache() || MENU_LAYOUT_DEFAULTS);

  useEffect(() => {
    app.fetchLoginLayout().then((data) => {
      if (data) {
        const merged = { ...MENU_LAYOUT_DEFAULTS, ...data };
        setMenuLayout(merged);
        try { localStorage.setItem(LAYOUT_CACHE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdministrator = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const activeCompanyId = Number(app.activeCompanyId || 0);
  const pendingCount = app.batches.filter((b) => (
    b.status === 'approval' && Number(b.companyId || 0) === activeCompanyId
  )).length;
  const NAV_LIVE = NAV.map((n) => (n.id === 'approve' ? { ...n, badge: pendingCount || undefined } : n));
  const navigateFromNav = (navId) => {
    if (navId === 'creditors') {
      const companyId = Number(app.activeCompanyId || 0);
      const selectedCompany = app.companies.find((c) => Number(c.id) === companyId) || null;
      const selectedIsActive = String(selectedCompany?.status || '').toLowerCase() === 'active';
      if (companyId > 0 && selectedIsActive) {
        app.navigate(ROUTES.COMPANY_CREDITORS, { companyId });
      } else {
        app.navigate(ROUTES.EMPTY, { shell: 'creditors' });
      }
      return;
    }
    app.navigate(ROUTE_FOR_NAV[navId]);
  };
  const cls = `sp-shell__aside${portaledDrawer && drawerOpen ? ' sp-shell__aside--drawer-open' : ''}`;
  return (
    <aside className={cls} aria-hidden={portaledDrawer ? !drawerOpen : undefined}>
      <div className="sp-shell__aside-brand">
        <div className="sp-shell__aside-brand-inner">
          <Wordmark size={17} brand={menuLayout.menu_wm_brand} accent={menuLayout.menu_wm_accent} />
          <div className="sp-shell__aside-brand-sub" dangerouslySetInnerHTML={{ __html: menuLayout.menu_brand_sub }} />
        </div>
        {canCollapse && (
          <button
            type="button"
            className="sp-shell__aside-collapse"
            aria-label="Minimizar menu"
            onClick={onCollapse}
          >
            <Icon name="chevron" size={12} />
          </button>
        )}
        <button type="button" className="sp-shell__aside-close" aria-label="Fechar menu" onClick={() => setAsideOpen(false)}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="sp-shell__aside-company">
        <div className="sp-shell__aside-label">Empresa ativa</div>
        <button type="button" onClick={() => { app.setCompanyPickerOpen(true); setAsideOpen(false); }} className="sp-shell__aside-company-btn">
          <div className="sp-shell__aside-company-text">
            <div className="sp-shell__aside-company-name">{app.activeCompany.name.replace(' S.A.', '').replace(' Ltda', '').replace(' Incorporações', ' Inc.')}</div>
            <div className="sp-mono sp-shell__aside-company-meta">sienge_id:{100 + app.activeCompany.id}</div>
          </div>
          <Icon name="chevronD" size={12} />
        </button>
      </div>
      <nav className="sp-shell__aside-nav">
        <div className="sp-shell__aside-nav-label">Navegação</div>
        {NAV_LIVE.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => {
              navigateFromNav(n.id);
              setAsideOpen(false);
            }}
            className={`sp-shell__aside-nav-btn${active === n.id ? ' is-active' : ''}`}
          >
            <Icon name={n.icon} size={14} />
            <span className="sp-shell__aside-nav-btn-label">{n.label}</span>
            {n.badge && <span className="sp-shell__aside-nav-badge">{n.badge}</span>}
          </button>
        ))}
      </nav>
      {isAdministrator && (
        <div className="sp-shell__aside-settings">
          <button
            type="button"
            className={`sp-shell__aside-settings-btn${active === ROUTES.SETTINGS ? ' is-active' : ''}`}
            onClick={() => {
              app.navigate(ROUTES.SETTINGS);
              setAsideOpen(false);
            }}
          >
            <Icon name="cog" size={14} />
            <span>Configurações</span>
          </button>
        </div>
      )}
      <div className={`sp-shell__aside-user${!isAdministrator ? ' sp-shell__aside-user--push' : ''}`}>
        <button
          type="button"
          className="sp-shell__aside-user-link"
          onClick={() => {
            app.navigate(ROUTES.PROFILE, { userId: app.currentUser?.id || undefined });
            setAsideOpen(false);
          }}
          title="Abrir detalhes da conta"
        >
          <Avatar name={app.currentUser.name} size={30} tint="#2A2D33" src={app.currentUser.avatar_data || ''} />
          <div className="sp-shell__aside-user-info">
            <div className="sp-shell__aside-user-name">{app.currentUser.name}</div>
            <div className="sp-mono sp-shell__aside-user-role">{String(app.currentUser.role || 'perfil').toUpperCase()}</div>
          </div>
        </button>
        <button
          type="button"
          className="sp-shell__aside-logout"
          onClick={() => { app.logout(); app.navigate(ROUTES.LOGIN); app.toast('info', 'Sessão encerrada', 'Até logo.'); }}
          title="Sair"
        >
          <Icon name="logout" size={14} />
        </button>
      </div>
    </aside>
  );
}

function PShell({ active, children, bleed = false }) {
  const app = useApp();
  const isAdministrator = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const [serverClock, setServerClock] = useState('--:--:-- - --/--/----');
  const [serverBaseMs, setServerBaseMs] = useState(null);
  const [serverBaseCapturedAtMs, setServerBaseCapturedAtMs] = useState(null);
  const [asideOpen, setAsideOpen] = useState(false);
  const [asideCollapsed, setAsideCollapsed] = useState(() => {
    try {
      return localStorage.getItem(ASIDE_COLLAPSED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const zoomStorageKey = `sp_zoom_${app.currentUser?.id || 'anon'}`;
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      return Number(localStorage.getItem(zoomStorageKey) || 100);
    } catch {
      return 100;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(zoomStorageKey, String(zoomLevel)); } catch { /* ignore */ }
  }, [zoomLevel]);

  const adjustZoom = (delta) => {
    setZoomLevel((prev) => Math.min(150, Math.max(50, prev + delta)));
  };

  const [drawerNarrow, setDrawerNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches,
  );

  useEffect(() => {
    setAsideOpen(false);
  }, [app.route]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = () => {
      if (mq.matches) setAsideOpen(false);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const sync = () => setDrawerNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!asideOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setAsideOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [asideOpen]);

  useEffect(() => {
    if (!asideOpen || !drawerNarrow) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [asideOpen, drawerNarrow]);

  useEffect(() => {
    try {
      localStorage.setItem(ASIDE_COLLAPSED_STORAGE_KEY, asideCollapsed ? '1' : '0');
    } catch {
      // ignore persistence errors
    }
  }, [asideCollapsed]);

  useEffect(() => {
    let cancelled = false;
    let syncTimerId = null;
    const updateClock = async () => {
      try {
        const baseUrl = String(app.debugBackendBaseUrl || '').replace(/\/+$/, '');
        const response = await fetch(`${baseUrl}/api/health`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const serverDate = new Date(data?.timestamp || '');
        if (!Number.isNaN(serverDate.getTime())) {
          setServerBaseMs(serverDate.getTime());
          setServerBaseCapturedAtMs(Date.now());
          setServerClock(formatServerClock(serverDate));
        }
      } catch {
        // keep previous clock when request fails
      }
    };
    updateClock();
    syncTimerId = window.setInterval(updateClock, SERVER_CLOCK_SYNC_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (syncTimerId) window.clearInterval(syncTimerId);
    };
  }, [app.debugBackendBaseUrl]);

  useEffect(() => {
    if (serverBaseMs == null || serverBaseCapturedAtMs == null) return undefined;
    const tickTimerId = window.setInterval(() => {
      const elapsed = Date.now() - serverBaseCapturedAtMs;
      const current = new Date(serverBaseMs + elapsed);
      setServerClock(formatServerClock(current));
    }, 1000);
    return () => {
      window.clearInterval(tickTimerId);
    };
  }, [serverBaseMs, serverBaseCapturedAtMs]);

  const activeCompanyId = Number(app.activeCompanyId || 0);
  const pendingCount = app.batches.filter((b) => (
    b.status === 'approval' && Number(b.companyId || 0) === activeCompanyId
  )).length;
  const NAV_LIVE = NAV.map((n) => (n.id === 'approve' ? { ...n, badge: pendingCount || undefined } : n));
  const navigateFromNav = (navId) => {
    if (navId === 'creditors') {
      const companyId = Number(app.activeCompanyId || 0);
      const selectedCompany = app.companies.find((c) => Number(c.id) === companyId) || null;
      const selectedIsActive = String(selectedCompany?.status || '').toLowerCase() === 'active';
      if (companyId > 0 && selectedIsActive) {
        app.navigate(ROUTES.COMPANY_CREDITORS, { companyId });
      } else {
        app.navigate(ROUTES.EMPTY, { shell: 'creditors' });
      }
      return;
    }
    app.navigate(ROUTE_FOR_NAV[navId]);
  };

  return (
    <>
      <div className={`sp-art sp-A sp-shell${drawerNarrow ? '' : (asideCollapsed ? ' sp-shell--rail-only' : ' sp-shell--no-rail')}`} style={{ zoom: `${zoomLevel}%`, '--sp-zoom-factor': zoomLevel / 100 }}>
        {(drawerNarrow || asideCollapsed) && (
          <div className="sp-shell__rail">
            <div className="sp-shell__logo">S</div>
            <button
              type="button"
              className="sp-shell__rail-menu"
              aria-label={asideOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={asideOpen}
              onClick={() => {
                if (drawerNarrow) {
                  setAsideOpen((o) => !o);
                } else {
                  setAsideCollapsed(false);
                }
              }}
            >
              <Icon name={(drawerNarrow && asideOpen) ? 'x' : 'menu'} size={17} />
            </button>
            <div className="sp-shell__rail-divider" />
            {NAV_LIVE.map((n) => (
              <button
                key={n.id}
                title={n.label}
                type="button"
                onClick={() => {
                  navigateFromNav(n.id);
                  setAsideOpen(false);
                }}
                className={`sp-shell__rail-btn${active === n.id ? ' is-active' : ''}`}
              >
                <Icon name={n.icon} size={17} />
                {n.badge && <span className="sp-shell__rail-badge" />}
                {active === n.id && <span className="sp-shell__rail-activebar" />}
              </button>
            ))}
            <div className="sp-shell__rail-footer">
              <button
                type="button"
                onClick={() => {
                  app.navigate(ROUTES.PROFILE, { userId: app.currentUser?.id || undefined });
                  setAsideOpen(false);
                }}
                className="sp-shell__rail-footer-btn sp-shell__rail-user-btn"
                title="Detalhes da conta"
                aria-label="Detalhes da conta"
              >
                <Avatar name={app.currentUser?.name || 'Usuário'} size={24} tint="#2A2D33" src={app.currentUser?.avatar_data || ''} />
              </button>
              {isAdministrator && (
                <button
                  type="button"
                  onClick={() => {
                    app.navigate(ROUTES.SETTINGS);
                    setAsideOpen(false);
                  }}
                  className={`sp-shell__rail-footer-btn${active === ROUTES.SETTINGS ? ' is-active' : ''}`}
                  title="Configurações do Sienge Pay"
                  aria-label="Configurações do Sienge Pay"
                >
                  <Icon name="cog" size={16} />
                </button>
              )}
              <button
                type="button"
                onClick={() => { app.logout(); app.navigate(ROUTES.LOGIN); app.toast('info', 'Sessão encerrada', 'Até logo.'); }}
                className="sp-shell__rail-footer-btn"
                title="Sair"
                aria-label="Sair"
              >
                <Icon name="logout" size={14} />
              </button>
            </div>
          </div>
        )}

        {!drawerNarrow && !asideCollapsed && (
          <ShellAside
            active={active}
            drawerOpen={false}
            portaledDrawer={false}
            setAsideOpen={setAsideOpen}
            canCollapse
            onCollapse={() => setAsideCollapsed(true)}
          />
        )}

        <header className="sp-shell__header">
          <button type="button" onClick={() => { app.setCompanyPickerOpen(true); setAsideOpen(false); }} className="sp-shell__header-company">
            <Icon name="building" size={13} />
            <span className="sp-shell__header-company-name">{app.activeCompany.name}</span>
            <Icon name="chevronD" size={12} />
          </button>
          <div className="sp-shell__header-actions">
            <Icon name="monitor" size={14} />
            <button type="button" className="sp-btn sp-btn-ghost" aria-label="Diminuir zoom" onClick={() => adjustZoom(-10)} disabled={zoomLevel <= 50}>-</button>
            <span className="sp-caps" style={{ minWidth: 36, textAlign: 'center' }}>{zoomLevel}%</span>
            <button type="button" className="sp-btn sp-btn-ghost" aria-label="Aumentar zoom" onClick={() => adjustZoom(10)} disabled={zoomLevel >= 150}>+</button>
            <div className="sp-shell__header-sep" />
            <button type="button" className="sp-btn sp-btn-ghost" aria-label="Horário do servidor"><Icon name="clock" size={14} /></button>
            <div className="sp-shell__header-sep" />
            <span className="sp-caps">{serverClock}</span>
          </div>
        </header>

        <main className={`sp-shell__main${bleed ? '' : ' sp-shell__main--padded'}`}>
          <ShellDebugPanel />
          {children}
        </main>
      </div>
      {drawerNarrow && createPortal(
        <ShellAside
          active={active}
          drawerOpen={asideOpen}
          portaledDrawer
          setAsideOpen={setAsideOpen}
          canCollapse={false}
          onCollapse={() => {}}
        />,
        document.body,
      )}
      {asideOpen && drawerNarrow && createPortal(
        <div
          className="sp-shell__backdrop"
          role="presentation"
          aria-hidden={false}
          onClick={() => setAsideOpen(false)}
        />,
        document.body,
      )}
    </>
  );
}

export { PShell };
